import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Building2, Users, CalendarDays, Plus, FileText, BarChart3 } from "lucide-react";
import { useEffect, useState } from "react";
import { MultiStepAddContractModal } from "@/components/MultiStepAddContractModal";
import { ContractStatus, BookingStatus, WelcomeEmailStatus, AdobeStatus, type Contract } from "@/types";
import { toast } from "sonner";
import { useContracts } from "@/contexts/ContractContext";
import { StatusCard } from "@/components/StatusCard";
import { getDashboardStats, type DashboardStats } from "@/api/dashboard";
// import { getAgreementsStatus } from "@/api/agreements";

export function Dashboard() {
  const { addContract } = useContracts();
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [isaddContractModalOpen, setIsaddContractModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch dashboard stats from API
  useEffect(() => {
    const fetchStats = async () => {
      try {
        setStatsLoading(true);
        const stats = await getDashboardStats();
        setDashboardStats(stats);
      } catch (error: any) {
        console.error('Failed to fetch dashboard stats:', error);
        toast.error('Failed to load dashboard statistics');
      } finally {
        setStatsLoading(false);
      }
    };

    fetchStats();
  }, []);

  const handleClientSubmit = async (newClientData: Omit<Contract, '_id'>) => {
    try {
      setIsLoading(true);

      // Add contract to context
      await addContract(newClientData);

      setIsaddContractModalOpen(false);
      
      // Refresh stats after adding a new contract
      try {
        const stats = await getDashboardStats();
        setDashboardStats(stats);
      } catch (error) {
        console.error('Failed to refresh dashboard stats:', error);
      }
      
    //  toast.success("Contract added successfully");
    } catch (error: any) {
      toast.error("Failed to create contract", {
        description: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Dashboard</h1>
        <div className="flex gap-2">
          <Button onClick={() => setIsaddContractModalOpen(true)} className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700">
            <Plus className="mr-2 h-4 w-4" />
            Add Contract
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 shadow-md hover:shadow-lg transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Units</CardTitle>
            <Building2 className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statsLoading ? '...' : (dashboardStats?.totalUnits || 0)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Available for booking</p>
          </CardContent>
        </Card>

        <StatusCard
          title="Contracts by Status"
          icon={<Users className="h-4 w-4" />}
          counts={dashboardStats?.contractStatusCounts || {}}
          isLoading={statsLoading}
          colorMap={{
            [ContractStatus.Pending]: "bg-cyan-100 text-cyan-800",
            [ContractStatus.Active]: "bg-slate-100 text-slate-800",
            [ContractStatus.Complete]: "bg-green-100 text-green-800",
            [ContractStatus.New]: "bg-orange-100 text-orange-800",
            [ContractStatus.Archived]: "bg-gray-100 text-gray-800"
          }}
          description="All contracts by current status"
        />

        <StatusCard
          title="Bookings by Status"
          icon={<CalendarDays className="h-4 w-4" />}
          counts={dashboardStats?.bookingStatusCounts || {}}
          isLoading={statsLoading}
          colorMap={{
            [BookingStatus.Active]: "bg-blue-100 text-blue-800",
            [BookingStatus.Inactive]: "bg-yellow-100 text-yellow-800",
            [BookingStatus.Cancelled]: "bg-red-100 text-red-800",
            [BookingStatus.Custom]: "bg-purple-100 text-purple-800"
          }}
          description="All bookings by their status"
        />

        <StatusCard
          title="Adobe by Status"
          icon={<FileText className="h-4 w-4" />}
          counts={dashboardStats?.adobeStatusCounts || {}}
          isLoading={statsLoading}
          colorMap={{
            none: "bg-gray-100 text-gray-800",
            [AdobeStatus.Draft]: "bg-indigo-100 text-indigo-800",
            [AdobeStatus.Sending]: "bg-cyan-100 text-cyan-800",
            [AdobeStatus.InProgress]: "bg-blue-100 text-blue-800",
            [AdobeStatus.Waiting]: "bg-yellow-100 text-yellow-800",
            [AdobeStatus.Completed]: "bg-green-100 text-green-800",
            [AdobeStatus.Canceled]: "bg-red-100 text-red-800",
            [AdobeStatus.Archived]: "bg-gray-100 text-gray-800"
          }}
          description="Adobe contract status distribution"
        />

        <StatusCard
          title="Offer Email Status"
          icon={<BarChart3 className="h-4 w-4" />}
          counts={dashboardStats?.welcomeEmailStatusCounts || {}}
          isLoading={statsLoading}
          colorMap={{
            [WelcomeEmailStatus.Pending]: "bg-yellow-100 text-yellow-800",
            [WelcomeEmailStatus.Sent]: "bg-green-100 text-green-800",
            [WelcomeEmailStatus.Opened]: "bg-blue-100 text-blue-800",
            [WelcomeEmailStatus.Failed]: "bg-red-100 text-red-800"
          }}
          description="Offer email status tracking"
        />
      </div>

      <MultiStepAddContractModal
        isOpen={isaddContractModalOpen}
        onClose={() => setIsaddContractModalOpen(false)}
        onSubmit={handleClientSubmit}
        isLoading={isLoading}
      />
    </div>
  );
}